import {
  Badge,
  Button,
  Card,
  CardHeader,
  LoadingSpinner,
  Popover,
  PopoverContent,
  PopoverTrigger,
  ScrollArea,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@ais-ui/ui-core';
import { ReportShipData, useSetSelectionData } from '@ais-ui/websockets';
import { ColumnDef, flexRender } from '@tanstack/react-table';
import { PiCaretLeftLight, PiCaretRightLight, PiTable } from 'react-icons/pi';
import React, { useEffect, useRef } from 'react';
import { FilterMenu } from './Filter';
import { TableFilter } from './filterTypes';
import { cn } from '@ais-ui/utils';
import { sortTableHeaders } from './sortTableHeaders';
import { filterTableHeaders } from './filterTableHeaders';
import { useLocalization } from '@ais-ui/locale-management';
import { useFetchOverlayData } from '../useMapReportOverlay';
import { useHorizontalScroll } from './useHorizontalScroll';
import { useShipTable } from './useShipTable';
import { useGetReportTableData } from './useGetReportTableData';

import { ReportsOptionDropDown } from './TableOptions';
import { useReportContext } from '../ReportContext';
import { useMapPanelContext } from '../../Map/MapPanel/MapPanelContext';
import { RowSelection } from './RowSelection';
import { useTableColumnsWithActions } from './hooks/useTableColumnsWithActions';
import { useSelectableReportType } from './hooks/useSelectableReportType';
import { EmptyListTable } from '../../../components/EmptyListTable';

interface Identifiable {
  id: number | string;
}

interface ReportShipTableProps {
  shipData: ReportShipData[] | undefined;
  className: string;
  jobId: string | undefined;
  reportType: string | null;
  isLoading: boolean;
}

// Define the available row actions

interface DataTableProps<TData, TValue> {
  columns: ColumnDef<TData, TValue>[];
  data: TData[];
  filters: TableFilter[];
  className?: string;
  jobId: string | undefined;
  isSelectableReportType: boolean;
  isLoading: boolean;
}

export const CollapsedTable = () => {
  const { messages } = useLocalization();
  const { setTableExpanded } = useMapPanelContext();

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          onClick={() => setTableExpanded(true)}
          variant="outline"
          size="icon"
        >
          <PiTable className="h-6 w-6" />
        </Button>
      </TooltipTrigger>
      <TooltipContent>
        {messages.reportsTableExpandButtonTooltip}
      </TooltipContent>
    </Tooltip>
  );
};

function DataTable<TData, TValue>({
  columns,
  data,
  filters,
  className,
  jobId,
  isSelectableReportType,
  isLoading,
}: DataTableProps<TData, TValue>) {
  const { messages } = useLocalization();
  const { tableExpanded, setTableExpanded } = useMapPanelContext();
  const viewportRef = useRef<HTMLDivElement | null>(null);

  const { handleScroll, showLeftScrollButton, showRightScrollButton } =
    useHorizontalScroll(viewportRef);

  const {
    table,
    rowsPerPage,
    appliedFilters,
    filteredData,
    onApplyFilter,
    onRemoveFilter,
    page,
    setPage,
    selectedRowIds,
    setSelectedRowIds,
  } = useShipTable(data, columns, isSelectableReportType);

  const fetchOverlayData = useFetchOverlayData();
  const { mutate } = useSetSelectionData(fetchOverlayData);
  const { showTracks } = useReportContext();

  useEffect(() => {
    const selectedIds = Object.keys(selectedRowIds)
      .map((id) => parseInt(id, 10))
      .filter((id) => selectedRowIds[id])
      .map((id) => ({ id }));

    if (!jobId) {
      return;
    }

    if (selectedIds.length > 0) {
      mutate({
        jobId: jobId,
        selections: selectedIds,
        show: showTracks,
        show_only_selected: true,
        show_sog_distribution: false,
        show_decimal_knots: false,
        ignore_sog_of_zero: false,
      });
    } else {
      mutate({
        jobId: jobId,
        selections: [],
        show: showTracks,
        show_only_selected: false,
        show_sog_distribution: false,
        show_decimal_knots: false,
        ignore_sog_of_zero: false,
      });
    }
  }, [selectedRowIds, mutate, jobId, showTracks]);

  if (!tableExpanded) {
    return null;
  }

  return (
    <Card
      className={cn(
        'relative flex flex-col h-fit w-full transition-all group/data-table px-1 pb-4 pt-1',
        className
      )}
    >
      <CardHeader className="px-7 py-4 text-lg font-normal text-muted-foreground">
        <div className="flex justify-between">
          <div className="flex space-x-4 items-end">
            <div className="flex space-x-4 items-center">
              <Popover>
                <PopoverTrigger asChild className="w-fit">
                  <Button variant="outline">
                    {messages.reportsTableFilterButtonText}
                  </Button>
                </PopoverTrigger>
                <PopoverContent side="bottom" align="start">
                  <FilterMenu
                    onApplyFilter={onApplyFilter}
                    filters={filters}
                  ></FilterMenu>
                </PopoverContent>
              </Popover>
              {Object.entries(appliedFilters).map(([column, appliedFilter]) => (
                <Badge
                  key={column}
                  variant="outline"
                  onClick={() => onRemoveFilter(column)}
                  className="group w-fit text-muted-foreground rounded-xl hover:bg-accent hover:text-accent-foreground cursor-pointer p-2"
                >
                  <div>
                    {column} {appliedFilter.operator} {appliedFilter.value}
                  </div>
                  <div className="inline-block opacity-0 pl-2 group-hover:opacity-100">
                    X
                  </div>
                </Badge>
              ))}
            </div>
            <ReportsOptionDropDown />
          </div>
          <div className="flex space-x-4 items-center">
            {!isLoading && data.length > 0 && (
              <>
                <div className="text-sm">
                  {page + 1} of {Math.ceil(filteredData.length / rowsPerPage)}
                </div>
                <div className="space-x-4">
                  <Button
                    variant="outline"
                    onClick={() => setPage((prev) => Math.max(prev - 1, 0))}
                    disabled={page === 0}
                  >
                    Previous
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() =>
                      setPage((prev) =>
                        Math.min(
                          prev + 1,
                          Math.ceil(filteredData.length / rowsPerPage) - 1
                        )
                      )
                    }
                    disabled={
                      page >= Math.ceil(filteredData.length / rowsPerPage) - 1
                    }
                  >
                    Next
                  </Button>
                </div>
              </>
            )}
          </div>
        </div>
        {!isLoading && data.length > 0 && (
          <RowSelection
            data={filteredData}
            selectedRowIds={selectedRowIds}
            onSelectAll={() => {
              setSelectedRowIds((prev) => {
                const newSelected: Record<string | number, boolean> = {
                  ...prev,
                };
                filteredData.forEach((row) => {
                  newSelected[(row as Identifiable).id] = true;
                });
                return newSelected;
              });
            }}
            onDeselectAll={() => {
              setSelectedRowIds((prev) => {
                const newSelected: Record<string | number, boolean> = {
                  ...prev,
                };
                filteredData.forEach((row) => {
                  delete newSelected[(row as Identifiable).id];
                });
                return newSelected;
              });
            }}
          ></RowSelection>
        )}
      </CardHeader>
      <div className="flex overflow-hidden">
        <Button
          onClick={() => handleScroll(-200)}
          className={cn(
            'p-0 mr-1',
            !showLeftScrollButton && 'opacity-0 pointer-events-none'
          )}
        >
          <PiCaretLeftLight className="h-4 w-4" />
        </Button>

        <ScrollArea
          infiniteScrollRef={viewportRef}
          className="mb-4 mx-2 flex-grow p-0 overflow-auto border rounded-md max-w-full"
        >
          <Table>
            <TableHeader className="sticky top-0 left-0 right-0 bg-background no-border">
              {table.getHeaderGroups().map((headerGroup) => (
                <TableRow key={headerGroup.id}>
                  {headerGroup.headers.map((header) => {
                    // Check if this is the actions column (usually the last one or has specific id)
                    const isActionsColumn =
                      header.id === 'actions' || header.column.id === 'actions';
                    return (
                      <TableHead
                        key={header.id}
                        className={cn(
                          'truncate',
                          isActionsColumn && 'w-[50px]'
                        )}
                      >
                        {header.isPlaceholder
                          ? null
                          : flexRender(
                              header.column.columnDef.header,
                              header.getContext()
                            )}
                      </TableHead>
                    );
                  })}
                </TableRow>
              ))}
            </TableHeader>
            {isLoading ? (
              <TableBody>
                <TableRow className="border-0">
                  <TableCell colSpan={columns.length}>
                    <div className="flex justify-center items-center min-h-[200px]">
                      <LoadingSpinner />
                    </div>
                  </TableCell>
                </TableRow>
              </TableBody>
            ) : (
              <TableBody>
                {table.getRowModel().rows?.length ? (
                  table.getRowModel().rows.map((row) => (
                    <TableRow
                      key={row.id}
                      data-state={row.getIsSelected() && 'selected'}
                      className="odd:bg-gray-50"
                    >
                      {row.getVisibleCells().map((cell) => {
                        // Check if this is the actions column
                        const isActionsColumn = cell.column.id === 'actions';
                        return (
                          <TableCell
                            key={cell.id}
                            className={cn(
                              'truncate',
                              isActionsColumn && 'w-[50px]'
                            )}
                          >
                            {flexRender(
                              cell.column.columnDef.cell,
                              cell.getContext()
                            )}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell
                      colSpan={columns.length}
                      className="h-24 text-center"
                    >
                      <div className="flex justify-center items-center min-h-[100px]">
                        <EmptyListTable
                          srcImage="empty_list_bro.png"
                          entity="result"
                        />
                      </div>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            )}
          </Table>
        </ScrollArea>
        <Button
          onClick={() => handleScroll(200)}
          className={cn(
            'p-0 ml-1',
            !showRightScrollButton && 'opacity-0 pointer-events-none'
          )}
        >
          <PiCaretRightLight className="h-4 w-4" />
        </Button>
      </div>
    </Card>
  );
}

export const ReportShipTable = React.memo(
  ({
    shipData,
    className,
    jobId,
    reportType,
    isLoading,
  }: ReportShipTableProps) => {
    const { filterByMapView } = useReportContext();

    const { columns, filters } = useTableColumnsWithActions({ shipData });

    useGetReportTableData(jobId, filterByMapView);

    const { isSelectableReportType } = useSelectableReportType(reportType);

    // Initialize with empty array if shipData is null/undefined to show loading state
    const safeShipData = shipData ?? [];

    return (
      <DataTable
        columns={columns}
        data={safeShipData}
        filters={filters}
        className={className}
        jobId={jobId}
        isSelectableReportType={isSelectableReportType}
        isLoading={isLoading}
      />
    );
  }
);

ReportShipTable.displayName = 'ReportShipTable';
